import React, { useState } from "react";
import AddUpdateTerminal from "./AddEditTerminal";
import { Terminal } from "../../types";

interface TerminalListProps {
  terminals: Terminal[];
  accountId: string;
  businessId: string;
  onRefresh?: () => void;
}

const TerminalList: React.FC<TerminalListProps> = ({
  terminals,
  accountId,
  businessId,
  onRefresh,
}) => {
  const [isAddTerminalOpen, setIsAddTerminalOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState<Terminal | null>(
    null
  );
  const [isEditing, setIsEditing] = useState(false);
  const [copiedTerminalId, setCopiedTerminalId] = useState<string | null>(null);

  const handleAddTerminal = (terminalData: {
    identifier: string;
    tid: string;
    serialNumber: string;
    isActive: boolean;
  }) => {
    // The API call is now handled inside AddUpdateTerminal component
    // This is just for any additional logic after successful addition
    console.log("Terminal added:", terminalData, "to account:", accountId);

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleEditTerminal = (terminal: Terminal) => {
    setSelectedTerminal(terminal);
    setIsEditing(true);
    setIsAddTerminalOpen(true);
  };

  const handleAddNewTerminal = () => {
    setSelectedTerminal(null);
    setIsEditing(false);
    setIsAddTerminalOpen(true);
  };

  const handleCloseTerminal = () => {
    setIsAddTerminalOpen(false);
    setSelectedTerminal(null);
    setIsEditing(false);
  };

  const handleSaveTerminal = (terminalData: {
    identifier: string;
    tid: string;
    serialNumber: string;
    isActive: boolean;
  }) => {
    if (selectedTerminal) {
      // Editing existing terminal
      const updatedTerminal = {
        ...selectedTerminal,
        identifier: terminalData.identifier,
        tid: terminalData.tid,
        serialNumber: terminalData.serialNumber,
        isActive: terminalData.isActive,
        lastUpdatedBy: "Current User",
        lastUpdatedDate: new Date(),
      };
      console.log(
        "Updating terminal:",
        updatedTerminal,
        "in account:",
        accountId
      );
    } else {
      // Adding new terminal
      handleAddTerminal(terminalData);
      return; // handleAddTerminal already handles success message
    }

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const copyToClipboard = async (text: string, terminalId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTerminalId(terminalId);
      // Clear the copied state after 2 seconds
      setTimeout(() => setCopiedTerminalId(null), 2000);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedTerminalId(terminalId);
      setTimeout(() => setCopiedTerminalId(null), 2000);
    }
  };

  if (!terminals || terminals.length === 0) {
    return (
      <>
        <div className="terminal-list-container">
          <h4>📟 Terminals</h4>
          <p className="terminal-list-message">
            No terminals found for this account
          </p>
          <button className="add-terminal-btn" onClick={handleAddNewTerminal}>
            ➕ Add New Terminal
          </button>
        </div>

        {/* Add Terminal Slide Panel */}
        <AddUpdateTerminal
          isOpen={isAddTerminalOpen}
          onClose={handleCloseTerminal}
          onSave={handleSaveTerminal}
          terminal={selectedTerminal}
          businessId={businessId}
          accountId={accountId}
          onRefresh={onRefresh}
        />
      </>
    );
  }

  return (
    <>
      <div className="terminal-list-container">
        <h4>📟 Terminals ({terminals.length})</h4>

        {showSuccess && (
          <div className="success-message">
            ✅ Terminal {isEditing ? "updated" : "added"} successfully!
          </div>
        )}

        <div className="payment-methods">
          {terminals.map((terminal) => (
            <div key={terminal.id} className="payment-method-card">
              <div className="payment-method-info">
                <div className="payment-method-icon">📟</div>
                <div className="payment-method-details">
                  <div className="payment-method-name">
                    {terminal.identifier}
                  </div>
                  <div
                    className="payment-method-number"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span
                      className="detail-value"
                      title={terminal.id || "Not specified"}
                      style={{
                        marginRight: "0.5rem",
                        marginLeft: "0",
                        paddingLeft: "0",
                      }}
                    >
                      <span className="detail-label">Identifier: </span>
                      {terminal.id
                        ? terminal.id.length > 15
                          ? terminal.id.substring(0, 15) + "..."
                          : terminal.id
                        : "Not specified"}
                    </span>
                    {terminal.id && (
                      <button
                        className="btn-icon btn-copy-icon"
                        onClick={() =>
                          copyToClipboard(terminal.id, terminal.id)
                        }
                        title={
                          copiedTerminalId === terminal.id
                            ? "Copied!"
                            : "Copy to clipboard"
                        }
                        style={{
                          fontSize: "0.875rem",
                          background:
                            copiedTerminalId === terminal.id
                              ? "#10b981"
                              : "transparent",
                          color:
                            copiedTerminalId === terminal.id
                              ? "white"
                              : "#667eea",
                          border: "1px solid",
                          borderColor:
                            copiedTerminalId === terminal.id
                              ? "#10b981"
                              : "#e5e7eb",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {copiedTerminalId === terminal.id ? "✓" : "⧉"}
                      </button>
                    )}
                  </div>
                  <div className="payment-method-number">
                    TID: {terminal.tid || "Not specified"}
                  </div>
                  <div className="payment-method-expiry">
                    Status: {terminal.isActive ? "Active" : "Inactive"} |
                    Serial: {terminal.serialNumber || "Not specified"}
                  </div>
                  {terminal.lastUpdatedBy && (
                    <div className="payment-method-extra">
                      Updated by: {terminal.lastUpdatedBy} |
                      {terminal.lastUpdatedDate
                        ? new Date(
                            terminal.lastUpdatedDate
                          ).toLocaleDateString()
                        : "No date"}
                    </div>
                  )}
                </div>
              </div>
              <div className="payment-method-actions">
                <span
                  className={`terminal-status-badge ${
                    terminal.isActive ? "status-active" : "status-inactive"
                  }`}
                >
                  {terminal.isActive ? "Active" : "Inactive"}
                </span>
                <button
                  className="btn-icon btn-edit-icon"
                  onClick={() => handleEditTerminal(terminal)}
                  title="Edit Terminal"
                >
                  ✏️
                </button>
                <button
                  className="btn-icon btn-remove-icon"
                  onClick={() => console.log("Remove terminal:", terminal.id)}
                  title="Remove Terminal"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
          <button className="add-terminal-btn" onClick={handleAddNewTerminal}>
            ➕ Add New Terminal
          </button>
        </div>
      </div>

      {/* Add Terminal Slide Panel */}
      <AddUpdateTerminal
        isOpen={isAddTerminalOpen}
        onClose={handleCloseTerminal}
        onSave={handleSaveTerminal}
        terminal={selectedTerminal}
        businessId={businessId}
        accountId={accountId}
        onRefresh={onRefresh}
      />
    </>
  );
};

export default TerminalList;
